import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import classNames from "classnames";
import _sortBy from "lodash/sortBy";
import _isEqual from "lodash/isEqual";
import FilterValues from "./FilterValues";
import Modal from "@components/Modal";
import { removeModal, addModal } from "@store/app/actions";
import InputText from "@components/Form/InputText";
import getConfig from "next/config";

const {
  publicRuntimeConfig: {
    settings: { websiteName },
  },
} = getConfig();

const isBricoStore = websiteName === "bricostore";

// Filter footer buttons: bricostore uses the pill button system; altex/MG keep the yellow style.
const FILTER_APPLY_CLASS = isBricoStore
  ? "py-2 px-5 text-14px text-center w-full text-white bg-red-brand rounded-full"
  : "py-2 px-5 text-14px border-2 border-transparent bg-filterYellow w-full rounded-bl-lg rounded-tr-lg";
const FILTER_RESET_CLASS = isBricoStore
  ? "py-2 px-5 text-14px text-center w-full bg-red-soft border border-red-brand text-red-brand rounded-full"
  : "py-2 px-5 text-14px border-2 border-filterYellow w-full rounded-bl-lg rounded-tr-lg";

const FilterGroup = ({
  filter,
  isModal,
  onFilterMobileApply,
  isLast,
  addModal,
  removeModal,
  activeFilters,
}) => {
  const isGeneral = filter.attribute_code === "general";
  const isReview = filter.attribute_code === "reviews_rating";

  // search input text
  const [searchTerm, setSearchTerm] = useState("");

  // values filtered by search
  const [filterValues, setFilterValues] = useState([...filter.filters]);

  const hasSearch = filter.filters.length > 7;

  const initialActiveSlugs = [...filter.filters]
    .filter((el) => el.isActive)
    .map((el) => `${el.attributeSlug || filter.slug}-${el.slug}`);

  // we keep a state of active values as we need it on mobile without modifying the original data
  const [activeSlugs, setActiveSlugs] = useState(initialActiveSlugs);

  const activeLength = activeSlugs.length;
  const sameActive = _isEqual(
    _sortBy(initialActiveSlugs),
    _sortBy(activeSlugs)
  );

  const filterValueMobileClick = (filterValue) => {
    const newActiveSlugs = [...activeSlugs];
    const slugKey = `${filterValue.attributeSlug || filter.slug}-${
      filterValue.slug
    }`;
    const activeIndex = newActiveSlugs.indexOf(slugKey);
    if (activeIndex > -1) {
      newActiveSlugs.splice(activeIndex, 1);
    } else {
      newActiveSlugs.push(slugKey);
    }
    setActiveSlugs(newActiveSlugs);
  };

  const generalSlugs =
    filter.slug === "general"
      ? filter.filters.map((filterValue) => filterValue.attributeSlug)
      : null;

  const applyFilters = () => {
    const filters = filter.filters.filter((el) =>
      activeSlugs.includes(`${el.attributeSlug || filter.slug}-${el.slug}`)
    );

    onFilterMobileApply(
      generalSlugs ? null : filter.slug,
      {
        ...filter,
        filters,
      },
      generalSlugs
    );
  };

  const deleteAllFilters = () => {
    onFilterMobileApply(
      generalSlugs ? null : filter.slug,
      {
        ...filter,
        filters: [],
      },
      generalSlugs
    );
  };

  useEffect(() => {
    if (searchTerm.length > 0) {
      const newValues = filter.filters.filter((el) =>
        el.label.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilterValues(newValues);
    } else {
      setFilterValues([...filter.filters]);
    }
  }, [searchTerm, filter.filters]);

  if (isModal)
    return (
      <li
        key={`${filter.slug}`}
        data-test="FILTERS_LIST_ITEM"
        className="border-b border-gray-400"
      >
        <button
          onClick={() => addModal(`filter-group-${filter.slug}`)}
          className="text-left flex flex-col justify-center w-full"
          style={{ height: 48 }}
        >
          <div
            className={classNames("text-14px", {
              "font-bold text-red-brand bd-claim": isBricoStore,
            })}
            data-test="FILTERS_ITEM_TITLE"
          >
            {isGeneral
              ? `Disponibilitate`
              : isReview
              ? "Nota minima"
              : filter.label}
          </div>
          {activeLength > 0 && (
            <div className="text-12px text-red-brand">
              {activeLength === 1
                ? "1 filtru selectat"
                : `${activeLength} filtre selectate`}
            </div>
          )}
        </button>
      </li>
    );

  return (
    <li key={`${filter.slug}`} data-test="FILTERS_LIST_ITEM">
      <div
        className={classNames("mb-2 leading-22 -tracking-0.22", {
          "font-medium": !isBricoStore,
          "font-bold text-red-brand bd-claim": isBricoStore,
        })}
        data-test="FILTERS_ITEM_TITLE"
      >
        {isGeneral
          ? `Disponibilitate`
          : isReview
          ? "Nota minima"
          : filter.label}
      </div>

      {hasSearch && (
        <div className="bg-white border-b border-gray-400 pb-4">
          <InputText
            className="block"
            meta={{}}
            input={{
              onChange: (e) => setSearchTerm(e.target.value),
              value: searchTerm,
            }}
            placeholder="Filtreaza"
          />
        </div>
      )}

      <ul
        className={`${
          hasSearch ? "overflow-y-auto pt-4 border-b border-gray-400" : ""
        } ${isLast ? "" : "mb-8"}`}
        style={{ height: hasSearch ? 278 : "auto" }}
        data-test="FILTERS_VALUE_LIST"
      >
        <FilterValues
          isReview={isReview}
          filters={filterValues}
          activeSlugs={initialActiveSlugs}
          parentSlug={filter.slug}
          isModal={false}
          filterValueMobileClick={filterValueMobileClick}
          activeFilters={activeFilters}
        />
      </ul>

      <Modal
        id={`filter-group-${filter.slug}`}
        header={filter.label}
        onBack={() => addModal("filters")}
        hasStickyHeader={true}
        hasStickyFooter={true}
        onRemoveModal={() => {
          // delay for the modal to actually close
          setTimeout(() => {
            setActiveSlugs(initialActiveSlugs);
            setSearchTerm("");
          }, 100);
        }}
        footer={
          <div>
            {!sameActive ? (
              <div className="flex space-x-2">
                <button
                  className={FILTER_APPLY_CLASS}
                  onClick={() => applyFilters()}
                >
                  Aplica filtrele
                </button>
                <button
                  className={FILTER_RESET_CLASS}
                  onClick={() => deleteAllFilters()}
                >
                  Sterge tot
                </button>
              </div>
            ) : (
              <div className="flex space-x-2">
                {activeLength > 0 && (
                  <button
                    className={FILTER_RESET_CLASS}
                    onClick={() => deleteAllFilters()}
                  >
                    Sterge tot
                  </button>
                )}
                <button
                  className={FILTER_RESET_CLASS}
                  onClick={() => removeModal()}
                >
                  Inchide
                </button>
              </div>
            )}
          </div>
        }
      >
        <div className="pt-16">
          {hasSearch && (
            <div className="absolute bg-white top-0 left-0 right-0 border-b border-gray-400 p-4">
              <InputText
                className="w-full"
                meta={{}}
                input={{
                  onChange: (e) => setSearchTerm(e.target.value),
                  value: searchTerm,
                }}
                placeholder="Filtreaza"
              />
            </div>
          )}
          <ul
            className={`${isLast ? "" : "mb-8"}`}
            data-test="FILTERS_VALUE_LIST"
          >
            <FilterValues
              isReview={isReview}
              filters={filterValues}
              activeSlugs={activeSlugs}
              parentSlug={filter.slug}
              isModal={true}
              filterValueMobileClick={filterValueMobileClick}
              activeFilters={activeFilters}
            />
          </ul>
        </div>
      </Modal>
    </li>
  );
};

export default connect(null, {
  removeModal,
  addModal,
})(FilterGroup);
